import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppSelector } from '@/store/hooks';

interface TagItem {
  _id: string;
  name: string;
  slug: string;
  status: 'active' | 'inactive' | '';
}

const Tags: React.FC = () => {
  const [tags, setTags] = useState<TagItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const { token } = useAppSelector((state) => state.auth);

  const baseUrl = import.meta.env.VITE_API_BASE_URL;

  // Fetch tags
  useEffect(() => {
    axios
      .get(`${baseUrl}/tags`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data.status && Array.isArray(res.data.data)) {
          setTags(res.data.data);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch tags:', err.response?.data || err.message);
      });
  }, [baseUrl, token]);

  console.log('Tags data:', tags);

  const filteredData = tags.filter(
    (tag) =>
      tag.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tag.slug?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredData.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Delete Tag
  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`${baseUrl}/tags/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setTags((prev) => prev.filter((tag) => tag._id !== id));
      alert('Tag deleted successfully ✅');
    } catch (error: any) {
      console.error('Error deleting tag:', error.response?.data || error.message);
      alert(error.response?.data?.message || 'Failed to delete tag');
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/blogs/tags/edit/${id}`);
  };

  const renderStatus = (status: boolean) => {
    console.log('Rendering status for:', status);
    if (status === true) {
      return <span className="badge bg-success text-white fs-14 rounded-2">Active</span>;
    } else if (status === false) {
      return <span className="badge bg-secondary text-white fs-14 rounded-2">Inactive</span>;
    }
    return null;
  };

  return (
    <div className="main-container container-fluid">
      {/* PAGE HEADER START */}
      <div className="page-header">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/dashboard">Home</a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Blog Tags
            </li>
          </ol>
        </div>
        <button
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={() => navigate('/blogs/tags/add')}
        >
          <Plus size={16} />
          Add Blog Tag
        </button>
      </div>
      {/* PAGE HEADER END */}

      <div className="row">
        <div className="col-12">
          <div className="card table-card">
            <div className="card-body">
              <div className="table-responsive">
                <div className="dataTables_wrapper dt-bootstrap5 no-footer">
                  {/* Controls Row */}
                  <div className="row">
                    <div className="col-sm-12 col-md-6">
                      <div className="dataTables_length">
                        <label>
                          Show{' '}
                          <select
                            name="entries_length"
                            className="form-select form-select-sm"
                            value={entriesPerPage}
                            onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>{' '}
                          entries
                        </label>
                      </div>
                    </div>
                    <div className="col-sm-12 col-md-6">
                      <div className="dataTables_filter">
                        <label>
                          Search:
                          <input
                            type="search"
                            className="form-control form-control-sm"
                            placeholder=""
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="row dt-row">
                    <div className="col-sm-12">
                      <table className="table dataTable no-footer">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>TAGS</th>
                            <th>Description</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentData.map((item, index) => (
                            <tr key={item._id} className={index % 2 === 0 ? 'odd' : 'even'}>
                              <td>{startIndex + index + 1}</td>
                              <td>{item.name}</td>
                              <td>{item.description}</td>
                              <td>{renderStatus(item.status)}</td>
                              <td>
                                <button
                                  onClick={() => handleEdit(item._id)}
                                  className="btn bg-primary-transparent text-primary p-2 rounded-2 text-center fs-16 me-2"
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => handleDelete(item._id)}
                                  className="btn bg-danger-transparent text-danger p-2 rounded-2 text-center fs-16"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  <div className="row">
                    <div className="col-sm-12 col-md-5">
                      <div className="dataTables_info">
                        Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of{' '}
                        {filteredData.length} entries
                      </div>
                    </div>
                    <div className="col-sm-12 col-md-7">
                      <div className="dataTables_paginate paging_simple_numbers">
                        <ul className="pagination">
                          <li className={`paginate_button page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <li
                              key={page}
                              className={`paginate_button page-item ${page === currentPage ? 'active' : ''}`}
                            >
                              <button className="page-link" onClick={() => setCurrentPage(page)}>
                                {page}
                              </button>
                            </li>
                          ))}
                          <li className={`paginate_button page-item next ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  {/* End Pagination */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tags;
